import { useState, useEffect, useRef } from 'react';
import EmojiMartPicker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { DEFAULT_REACTIONS } from '@goodchat/shared/constants';
import type { CustomEmoji } from '@goodchat/shared/types';

interface EmojiPickerPopoverProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  customEmoji?: CustomEmoji[];
}

export function EmojiPickerPopover({ onSelect, onClose, customEmoji }: EmojiPickerPopoverProps) {
  const [showFullPicker, setShowFullPicker] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Detect dark mode: check for .dark class on <html> first (manual toggle),
  // then fall back to prefers-color-scheme media query (system preference)
  const getTheme = (): 'dark' | 'light' => {
    if (typeof document === 'undefined') return 'light';
    if (document.documentElement.classList.contains('dark')) return 'dark';
    if (document.documentElement.classList.contains('light')) return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };
  const theme = getTheme();

  const visibleCustom = customEmoji && customEmoji.length > 0 ? customEmoji : null;

  return (
    <div
      ref={containerRef}
      className="absolute right-0 top-8 z-50"
    >
      {!showFullPicker ? (
        // Quick-access: custom emoji + 6 default reactions + "more" button
        <div className="rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {visibleCustom && (
            <div className="flex flex-wrap gap-1 border-b border-gray-100 p-2 dark:border-gray-700">
              {visibleCustom.slice(0, 12).map((ce) => {
                const imgUrl = (ce as unknown as { url?: string; imageUrl?: string }).url ?? (ce as unknown as { url?: string; imageUrl?: string }).imageUrl ?? '';
                return (
                  <button
                    key={ce.id}
                    onClick={() => {
                      onSelect(`:${ce.name}:`);
                      onClose();
                    }}
                    className="rounded p-1 transition-transform hover:scale-125 hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label={`React with :${ce.name}:`}
                    title={`:${ce.name}:`}
                  >
                    <img src={imgUrl} alt={ce.name} className="h-[22px] w-[22px] object-contain" />
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex items-center gap-1 p-2">
            {DEFAULT_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => {
                  onSelect(emoji);
                  onClose();
                }}
                className="rounded p-1 text-lg transition-transform hover:scale-125 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label={`React with ${emoji}`}
              >
                {emoji}
              </button>
            ))}
            <button
              onClick={() => setShowFullPicker(true)}
              className="ml-0.5 flex h-8 w-8 items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              aria-label="Browse all emojis"
              title="More emojis"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        // Full emoji-mart picker with search, categories, skin tones, and frequently used
        <div className="rounded-lg shadow-lg">
          {visibleCustom && (
            <div className="border-b border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Custom</p>
              <div className="flex flex-wrap gap-1">
                {visibleCustom.map((ce) => {
                  const imgUrl = (ce as unknown as { url?: string; imageUrl?: string }).url ?? (ce as unknown as { url?: string; imageUrl?: string }).imageUrl ?? '';
                  return (
                    <button
                      key={ce.id}
                      onClick={() => {
                        onSelect(`:${ce.name}:`);
                        onClose();
                      }}
                      className="rounded p-1 transition-transform hover:scale-110 hover:bg-gray-100 dark:hover:bg-gray-700"
                      title={`:${ce.name}:`}
                    >
                      <img src={imgUrl} alt={ce.name} className="h-[22px] w-[22px] object-contain" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <EmojiMartPicker
            data={data}
            onEmojiSelect={(emoji: { native: string }) => {
              onSelect(emoji.native);
              onClose();
            }}
            onClickOutside={onClose}
            theme={theme}
            previewPosition="none"
            skinTonePosition="search"
            perLine={8}
            emojiSize={22}
            emojiButtonSize={32}
            maxFrequentRows={2}
            autoFocus
          />
        </div>
      )}
    </div>
  );
}
